import {
    faEdit,
    faFileAlt,
    faPlay,
    faRedo,
    faStop,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import LogModal from '../../components/Logs/LogModal';
import Menu from '../../components/Menu/Menu';
import SelectSymbol from '../../components/SelectSymbol/SelectSymbol';
import Toast from '../../components/Toast/Toast';
import {
    getMonitorStatus,
    startMonitor,
    stopMonitor,
} from '../../services/MonitorsService';
import MemoryTable from '../../components/Memory/MemoryTable';
import CandleChart from '../Dashboard/CandleChart';
import MonitorModal from './MonitorModal/MonitorModal';

function formatAge(ms) {
    if (ms === null || ms === undefined) return 'never';
    const seconds = Math.max(1, Math.round(ms / 1000));
    if (seconds < 90) return `${seconds}s ago`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 90) return `${minutes}m ago`;
    return `${Math.round(minutes / 60)}h ago`;
}

function getMonitorTitle(monitor) {
    if (!monitor) return '';
    return (
        monitor.type +
        (monitor.symbol && monitor.symbol !== '*' ? ' ' + monitor.symbol : '') +
        (monitor.interval ? '_' + monitor.interval : '')
    );
}

function getStatusBadge(isActive) {
    return isActive ? (
        <span className="badge bg-success ms-1">RUNNING</span>
    ) : (
        <span className="badge bg-danger ms-1">STOPPED</span>
    );
}

function MonitorStatus() {
    const { id } = useParams();

    const [status, setStatus] = useState(null);
    const [symbol, setSymbol] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [refresh, setRefresh] = useState(0);
    const [editMonitor, setEditMonitor] = useState({});
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        setIsLoading(true);
        getMonitorStatus(id, symbol || undefined)
            .then(result => {
                setStatus(result);
                setUpdatedAt(new Date());
                setIsLoading(false);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
                setIsLoading(false);
            });
    }, [id, symbol, refresh]);

    function onReloadClick() {
        setRefresh(Date.now());
    }

    function onStartClick() {
        startMonitor(id)
            .then(() => setRefresh(Date.now()))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onStopClick() {
        stopMonitor(id)
            .then(() => setRefresh(Date.now()))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onEditClick() {
        setEditMonitor({ ...status.monitor });
    }

    function getEngineAlert() {
        const engine = status && status.engine;
        if (!engine) return <></>;

        return engine.ok ? (
            <div className="alert alert-success d-flex justify-content-between py-2">
                <span>
                    Engine running. Last market event{' '}
                    {formatAge(engine.marketEventAgeMs)}.
                </span>
                {engine.evalErrorStreak > 0 ? (
                    <span className="fw-bold">
                        {engine.evalErrorStreak} evaluation errors in a row!
                    </span>
                ) : (
                    <></>
                )}
            </div>
        ) : (
            <div className="alert alert-danger py-2">
                Engine stalled or Redis unreachable. Last market event{' '}
                {formatAge(engine.marketEventAgeMs)}. Orders and automations may
                not be running!
            </div>
        );
    }

    function getConfigFields(monitor) {
        const fields = [
            ['Type', monitor.type],
            ['Symbol', monitor.symbol],
            ['Interval', monitor.interval || '-'],
            ['Broadcast', monitor.broadcastLabel || '-'],
        ];

        return (
            <>
                {fields.map(([label, value]) => (
                    <div className="col-md-3 mb-3" key={label}>
                        <div className="form-group">
                            <label>{label}:</label>
                            <div>{value}</div>
                        </div>
                    </div>
                ))}
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Indexes:</label>
                        <div>
                            {monitor.indexes
                                ? monitor.indexes.split(',').map(index => (
                                      <span
                                          className="badge bg-secondary me-1 mb-1"
                                          key={index}
                                      >
                                          {index}
                                      </span>
                                  ))
                                : '-'}
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Logs:</label>
                        <div>{monitor.logs ? 'Enabled' : 'Disabled'}</div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Created:</label>
                        <div>
                            {new Date(monitor.createdAt).toLocaleString()}
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Updated:</label>
                        <div>
                            {new Date(monitor.updatedAt).toLocaleString()}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    function getEmptyText(monitor) {
        return !monitor.isActive
            ? 'No data in the memory yet. The monitor is stopped, start it to feed the memory.'
            : monitor.symbol === '*'
              ? 'No data in the memory yet. Pick a symbol above to inspect a market.'
              : 'No data in the memory yet. Wait for the next stream event.';
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="card card-body border-0 shadow mb-4 mt-4">
                    <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                        <div className="d-block mb-4 mb-md-0">
                            <h2 className="h4">
                                {getMonitorTitle(status && status.monitor)}
                                {status ? (
                                    getStatusBadge(status.monitor.isActive)
                                ) : (
                                    <></>
                                )}
                            </h2>
                            {updatedAt ? (
                                <small className="text-gray-500">
                                    Updated at {updatedAt.toLocaleTimeString()}
                                </small>
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className="btn-toolbar mb-2 mb-md-0">
                            <div className="d-inline-flex align-items-center">
                                {status && status.monitor.symbol === '*' ? (
                                    <div className="me-2">
                                        <SelectSymbol
                                            symbol={symbol}
                                            onChange={event =>
                                                setSymbol(event.target.value)
                                            }
                                        />
                                    </div>
                                ) : (
                                    <></>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-gray-800 d-inline-flex align-items-center me-2"
                                    onClick={onReloadClick}
                                    disabled={isLoading}
                                    title="Reload the current data"
                                >
                                    <FontAwesomeIcon
                                        icon={faRedo}
                                        className={
                                            'icon icon-xs me-2' +
                                            (isLoading ? ' fa-spin' : '')
                                        }
                                    />
                                    Reload
                                </button>
                                {status &&
                                !status.monitor.isSystemMon &&
                                !status.monitor.isActive ? (
                                    <button
                                        type="button"
                                        className="btn btn-success me-2"
                                        onClick={onStartClick}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPlay}
                                            className="icon icon-xs me-1"
                                        />
                                        Start
                                    </button>
                                ) : (
                                    <></>
                                )}
                                {status &&
                                !status.monitor.isSystemMon &&
                                status.monitor.isActive ? (
                                    <button
                                        type="button"
                                        className="btn btn-danger me-2"
                                        onClick={onStopClick}
                                    >
                                        <FontAwesomeIcon
                                            icon={faStop}
                                            className="icon icon-xs me-1"
                                        />
                                        Stop
                                    </button>
                                ) : (
                                    <></>
                                )}
                                {status && !status.monitor.isSystemMon ? (
                                    <button
                                        id={'edit' + id}
                                        type="button"
                                        className="btn btn-secondary me-2"
                                        title="Edit this Monitor"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modalMonitor"
                                        onClick={onEditClick}
                                    >
                                        <FontAwesomeIcon
                                            icon={faEdit}
                                            className="icon icon-xs me-1"
                                        />
                                        Edit
                                    </button>
                                ) : (
                                    <></>
                                )}
                                {status && status.monitor.logs ? (
                                    <button
                                        type="button"
                                        className="btn btn-info me-2"
                                        title="Logs from this Monitor"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modalLogs"
                                    >
                                        <FontAwesomeIcon
                                            icon={faFileAlt}
                                            className="icon icon-xs me-1"
                                        />
                                        Logs
                                    </button>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                    </div>
                    {status ? (
                        <>
                            {getEngineAlert()}
                            <div className="form-group mb-3">
                                <div className="row">
                                    {getConfigFields(status.monitor)}
                                </div>
                            </div>
                        </>
                    ) : (
                        <></>
                    )}
                </div>
                {status ? (
                    <MemoryTable
                        memory={status.memory}
                        emptyText={getEmptyText(status.monitor)}
                    />
                ) : (
                    <></>
                )}
                {status &&
                status.monitor.type === 'CANDLES' &&
                status.monitor.symbol &&
                status.monitor.symbol !== '*' ? (
                    <CandleChart
                        symbol={status.monitor.symbol}
                        interval={status.monitor.interval || '1m'}
                    />
                ) : (
                    <></>
                )}
                <Footer />
            </main>
            <MonitorModal data={editMonitor} onSubmit={onReloadClick} />
            <LogModal file={id ? 'M' + id : ''} />
            <Toast
                type={notification.type}
                text={notification.text}
                onShow={() => setNotification({})}
            />
        </>
    );
}

export default MonitorStatus;
